/**
 * @module app.detour.style
 */
import {Icon, Stroke, Style} from 'ol/style';

const WARNING_SVG_SRC = 'img/detour/warning';
const BICYCLE_BAN_SVG_SRC = 'img/detour/bicycle_ban';
const PEDESTRIAN_BAN_SVG_SRC = 'img/detour/pedestrian_ban';
const SKATING_BAN_SVG_SRC = 'img/detour/skating_ban';
const TRAFFIC_BAN_SVG_SRC = 'img/detour/traffic_ban';
const LINE_WIDTH_CLOSEST = 12;
const LINE_WIDTH_FAR = 10;

// style cache, required by ngeo vector encoder.
const lineStyleCache = {};


/**
 * @param {boolean} is3d
 * @param {(function(): (number|null))=} highlightedIdFunction
 * @param {string} type
 * @return {!function((null|ol.Feature), number)}
 */
export function styleFactory(is3d, highlightedIdFunction, type) {
  /**
   * @param {ol.Feature} feature
   * @param {number} resolution
   * @return {!Array<ol.style.Style>}
   */
  function styleFunction(feature, resolution) {
    const styleName = feature.getGeometry().getType();
    if ((!is3d && resolution > 20) || type !== styleName) {
      return [];
    }
    const highlighted = highlightedIdFunction && highlightedIdFunction() === feature.get('id');
    const detourAttrs = feature.getProperties();
    if (styleName === 'Point') {
      if (detourAttrs.symbol_intermediate && detourAttrs.is_intermediate_point) {
        return getIconSrc(detourAttrs.symbol_intermediate, highlighted, is3d);
      }
      if (detourAttrs.symbol_start_end && detourAttrs.is_start_end_point) {
        return getIconSrc(detourAttrs.symbol_start_end, highlighted, is3d);
      }
      return [];
    } else {
      return getLineStyle(detourAttrs.is_closed_way, resolution, is3d);
    }
  }
  return styleFunction;
}

function getIconSrc(type, highlighted, is3d) {
  let src = '';
  switch (type) {
    case 'warning':
      src = WARNING_SVG_SRC;
      break;
    case 'pedestrian_ban':
      src = PEDESTRIAN_BAN_SVG_SRC;
      break;
    case 'bicycle_ban':
      src = BICYCLE_BAN_SVG_SRC;
      break;
    case 'traffic_ban':
      src = TRAFFIC_BAN_SVG_SRC;
      break;
    case 'skating_ban':
      src = SKATING_BAN_SVG_SRC;
      break;
    default:
      break;
  }

  if (!src) {
    return null;
  }

  return new Style({
    image: new Icon({
      src: highlighted ? `${src}_h.svg` : `${src}.svg`,
      anchor: is3d ? [0.5, 1] : [0.5, 0.5]
    })
  });
}

function getLineStyle(closedWay, resolution, is3D) {
  const width2D = resolution > 5 ? LINE_WIDTH_FAR : LINE_WIDTH_CLOSEST;
  const width3D = 5;
  const width = is3D ? width3D : width2D;
  const key = `${width}_${closedWay}`;
  let style = lineStyleCache[key];
  if (!style) {
    if (closedWay) {
      style = [
        new Style({
          stroke: new Stroke({
            width: width,
            color: [255, 255, 255]
          })
        }),
        new Style({
          stroke: new Stroke({
            width: width,
            color: [255, 0, 0],
            lineDash: [10, 30],
            lineCap: 'square',
            lineJoin: 'miter',
            miterLimit: 10
          })
        })
      ];
    } else {
      style = [
        new Style({
          stroke: new Stroke({
            width: width,
            color: [249, 181, 0]
          })
        })
      ];
    }
    lineStyleCache[key] = style;
  }
  return style;
}
